import { ChangeEvent } from 'react'
import { Checkbox, Flex, Form, Input, Typography } from 'antd'
import { ErrorMessage } from '@hookform/error-message'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { inputNumber } from 'src/app/shared/utils'

import './index.scss'

interface Props {
  errors: any
  control: any
  setValue?: any
  trigger?: any
  clearErrors?: any
  disabled?: boolean
  onRangeChange?: () => void
}

/**
 * US32690 - optional Row Skip Range of a File Mapping ("Skip Rows from X to Y").
 * Shared by the Add / Edit / Copy / View screens so configuration, preview and processing always
 * read the range the same way. Leaving the checkbox unticked means "skip no rows", which keeps the
 * existing file interpretation exactly as it is today.
 */
function SkipRowsRangeField(props: Readonly<Props>) {
  const { t } = useTranslation()

  const { errors, control, setValue, trigger, clearErrors, disabled = false, onRangeChange } = props

  const skipRowsEnabled = useWatch({ control, name: 'skipRowsEnabled' })
  const isRangeDisabled = disabled || !skipRowsEnabled

  const handleRangeChange = (field: any, event: ChangeEvent<HTMLInputElement>, fieldToRevalidate?: string) => {
    const value = inputNumber(event)
    field.onChange(value === '' ? null : Number(value), { shouldValidate: true })
    fieldToRevalidate && trigger?.(fieldToRevalidate)
    onRangeChange?.()
  }

  return (
    <Flex align='center' gap={8} className='skip-rows-range'>
      <Controller
        name='skipRowsEnabled'
        control={control}
        render={({ field }) => (
          <Checkbox
            {...field}
            checked={field.value}
            disabled={disabled}
            onChange={(e) => {
              field?.onChange(e)
              if (!e.target.checked) {
                setValue?.('skipRowsFrom', null)
                setValue?.('skipRowsTo', null)
                clearErrors?.(['skipRowsFrom', 'skipRowsTo'])
              }
              onRangeChange?.()
            }}
          >
            {t('FileMappingSetup:addFileMapping.label.skipRows')}
          </Checkbox>
        )}
      />

      <Form.Item className='skip-rows-range-item'>
        <Controller
          name='skipRowsFrom'
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              status={!!fieldState.error ? 'error' : ''}
              placeholder={t('FileMappingSetup:addFileMapping.placeholder.from')}
              maxLength={2}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleRangeChange(field, e, 'skipRowsTo')}
            />
          )}
          disabled={isRangeDisabled}
        />
        <Typography className='error-message'>
          <ErrorMessage errors={errors} name='skipRowsFrom' />
        </Typography>
      </Form.Item>

      <Typography className='skip-rows-range-separator'>
        {t('FileMappingSetup:addFileMapping.label.skipRowsTo')}
      </Typography>

      <Form.Item className='skip-rows-range-item'>
        <Controller
          name='skipRowsTo'
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              status={!!fieldState.error ? 'error' : ''}
              placeholder={t('FileMappingSetup:addFileMapping.placeholder.to')}
              maxLength={2}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleRangeChange(field, e)}
            />
          )}
          disabled={isRangeDisabled}
        />
        <Typography className='error-message'>
          <ErrorMessage errors={errors} name='skipRowsTo' />
        </Typography>
      </Form.Item>
    </Flex>
  )
}

export default SkipRowsRangeField
